import type { LibraryBlock } from "../build";
import {
  BLACK_GLASS,
  ellipse,
  icon,
  iconPill,
  motion,
  pill,
  rect,
  SHADOW,
  text,
  WHITE,
} from "./helpers";

// ---------------------------------------------------------------------------
// Hinweise: advertising labels (required in Germany), sound on, spoilers …
// ---------------------------------------------------------------------------

/** Labels stay calm: a plain fade, readable from the first frame. */
const calm = motion("fade", { duration: 250 });

export const NOTICES: LibraryBlock[] = [
  {
    id: "hinweis-anzeige",
    title: "Anzeige",
    category: "hinweis",
    tags: ["Werbung", "Kennzeichnung", "Werbekennzeichnung", "Ad", "Sponsored"],
    width: 300,
    height: 80,
    anchor: "top-left",
    duration: 8000,
    nodes: [
      ...pill(0, 0, 300, 80, "ANZEIGE", BLACK_GLASS, 34, calm, { color: WHITE, radius: "sm" }),
    ],
  },
  {
    id: "hinweis-werbung",
    title: "Werbung · Kooperation",
    category: "hinweis",
    tags: ["Anzeige", "Kennzeichnung", "Kooperation", "Sponsoring", "Partner"],
    width: 720,
    height: 80,
    anchor: "top-left",
    duration: 8000,
    nodes: [
      rect({ x: 0, y: 0, w: 720, h: 80, fill: BLACK_GLASS, radius: "sm", name: "Fläche", ...calm }),
      text({
        text: "WERBUNG",
        style: "subline",
        size: 32,
        color: WHITE,
        x: 26,
        y: 0,
        w: 210,
        h: 80,
        align: "center",
        ...calm,
      }),
      rect({ x: 252, y: 20, w: 2, h: 40, fill: "#ffffff80", name: "Trenner", ...calm }),
      text({
        text: "in Kooperation mit @marke",
        style: "caption",
        size: 30,
        color: "#ffffffe6",
        x: 274,
        y: 0,
        w: 426,
        h: 80,
        ...calm,
      }),
    ],
  },
  {
    id: "hinweis-unbezahlt",
    title: "Unbezahlte Werbung",
    category: "hinweis",
    tags: ["Werbung", "Kennzeichnung", "Markennennung", "unbezahlt"],
    width: 560,
    height: 80,
    anchor: "top-left",
    duration: 8000,
    nodes: [
      ...pill(0, 0, 560, 80, "UNBEZAHLTE WERBUNG", "background/90", 30, calm, {
        color: "text",
        radius: "sm",
      }),
    ],
  },
  {
    id: "hinweis-affiliate",
    title: "Affiliate-Link",
    category: "hinweis",
    tags: ["Werbung", "Provision", "Link", "Kennzeichnung", "Sternchen"],
    width: 960,
    height: 80,
    anchor: "bottom",
    duration: 6000,
    nodes: [
      rect({ x: 0, y: 0, w: 960, h: 80, fill: BLACK_GLASS, radius: "sm", name: "Fläche", ...calm }),
      text({
        text: "*Affiliate-Link: Für dich bleibt der Preis gleich.",
        style: "caption",
        size: 30,
        color: WHITE,
        x: 24,
        y: 0,
        w: 912,
        h: 80,
        align: "center",
        ...calm,
      }),
    ],
  },
  {
    id: "hinweis-ton-an",
    title: "Ton an",
    category: "hinweis",
    tags: ["Sound", "Lautsprecher", "Musik", "laut", "Audio"],
    width: 480,
    height: 130,
    anchor: "top",
    duration: 3500,
    only: "video",
    nodes: iconPill(0, 0, 480, 130, {
      icon: "speaker-high",
      label: "Ton an!",
      fill: "accent",
      badge: "background",
      iconColor: "accent",
      size: 52,
      style: "heading-2",
      move: motion("pop", { loop: "pulse" }),
    }),
  },
  {
    id: "hinweis-bis-zum-ende",
    title: "Bis zum Ende schauen",
    category: "hinweis",
    tags: ["warten", "Ende", "Hook", "dranbleiben", "Sanduhr"],
    width: 780,
    height: 130,
    anchor: "top",
    duration: 4000,
    only: "video",
    nodes: iconPill(0, 0, 780, 130, {
      icon: "hourglass",
      label: "Schau bis zum Ende!",
      fill: "primary",
      badge: "background",
      iconColor: "primary",
      size: 46,
      style: "heading-2",
      move: motion("slide-down"),
      iconMove: motion("spin", { delay: 200, loop: "wiggle" }),
    }),
  },
  {
    id: "hinweis-spoiler",
    title: "Achtung Spoiler",
    category: "hinweis",
    tags: ["Warnung", "Spoiler", "Achtung", "Vorsicht"],
    width: 800,
    height: 290,
    anchor: "center",
    duration: 3500,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 800,
        h: 290,
        fill: "#111111e6",
        radius: "md",
        name: "Fläche",
        ...motion("zoom"),
      }),
      icon({
        icon: "warning",
        x: 330,
        y: 30,
        w: 140,
        h: 140,
        color: "#facc15",
        name: "Warnung",
        ...motion("drop", { delay: 150, duration: 700, loop: "wiggle" }),
      }),
      text({
        text: "ACHTUNG SPOILER",
        style: "heading-1",
        size: 72,
        color: "#facc15",
        x: 30,
        y: 180,
        w: 740,
        h: 90,
        align: "center",
        ...motion("pop", { delay: 300 }),
      }),
    ],
  },
  {
    id: "hinweis-teil-2",
    title: "Teil 2 folgt",
    category: "hinweis",
    tags: ["Fortsetzung", "Serie", "demnächst", "weiter"],
    width: 640,
    height: 130,
    anchor: "bottom",
    duration: 4000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 640,
        h: 130,
        fill: BLACK_GLASS,
        radius: "full",
        name: "Pille",
        ...motion("slide-up"),
      }),
      text({
        text: "Teil 2 folgt",
        style: "heading-2",
        size: 52,
        color: WHITE,
        x: 50,
        y: 0,
        w: 430,
        h: 130,
        ...motion("slide-up", { delay: 100 }),
      }),
      ellipse({
        x: 510,
        y: 15,
        w: 100,
        h: 100,
        fill: "accent",
        name: "Kreis",
        ...motion("pop", { delay: 250 }),
      }),
      icon({
        icon: "arrow-right",
        x: 535,
        y: 40,
        w: 50,
        h: 50,
        color: "on:accent",
        name: "Pfeil",
        ...motion("pop", { delay: 250, loop: "wiggle" }),
      }),
    ],
  },
  {
    id: "hinweis-nach-oben",
    title: "Nach oben wischen",
    category: "hinweis",
    tags: ["Swipe", "wischen", "mehr", "Link", "Story"],
    width: 520,
    height: 260,
    anchor: "bottom",
    duration: 4000,
    nodes: [
      icon({
        icon: "caret-double-up",
        x: 190,
        y: 0,
        w: 140,
        h: 140,
        color: WHITE,
        shadow: SHADOW,
        name: "Pfeile",
        ...motion("fly-up", { duration: 600, loop: "float" }),
      }),
      ...pill(
        0,
        150,
        520,
        100,
        "Mehr erfahren",
        "background",
        40,
        motion("slide-up", { delay: 200 }),
      ),
    ],
  },
];
